import React, { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SettingsIcon from "@mui/icons-material/Settings";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import AssessmentIcon from "@mui/icons-material/Assessment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// ============================================================
// EXISTING ERP SETUP MODULES
// ============================================================

import SocietyClient from "./SocietyClient";
import Client from "../../admin/Setup/Client";

// ============================================================
// RBAC MODULES
// ============================================================

import Modules from "./RBAC/Modules";
import ModuleTabs from "./RBAC/ModuleTabs";
import ModuleButtons from "./RBAC/ModuleButtons";
import ButtonActions from "./RBAC/ButtonActions";

import Roles from "./RBAC/Roles";
import RolePermissionMatrix from "./RBAC/RolePermissionMatrix";

import UserCollegeAssignment from "./RBAC/UserCollegeAssignment";
import UserCollegeRoleAssignment from "./RBAC/UserCollegeRoleAssignment";
import UserCollegeRoleBulkAssignment from "./RBAC/UserCollegeRoleBulkAssignment";
// ============================================================
// ERP SETUP
// ============================================================

function ERPSetup() {
  const [loading, setLoading] = useState(true);

  const [activeView, setActiveView] = useState("home");

  // ============================================================
  // 0 = UPLOAD
  // 1 = SETUP
  // 2 = ACTIONS
  // 3 = REPORTS
  // ============================================================

  const [tabValue, setTabValue] = useState(1);

  const navigate = useNavigate();

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    setLoading(false);
  }, []);

  // ============================================================
  // TAB CHANGE
  // ============================================================

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setActiveView("home");
  };

  // ============================================================
  // NAVIGATION
  // ============================================================

  const handleNavigate = (path) => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      navigate("/authentication/sign-in");
      return;
    }

    setActiveView(path);
  };

  // ============================================================
  // BACK
  // ============================================================

  const handleBack = () => {
    setActiveView("home");
  };

  // ============================================================
  // REUSABLE MENU BUTTON
  // ============================================================

  // ============================================================
  // REUSABLE COLORFUL MENU BUTTON
  // ============================================================

  const MenuCard = ({ title, icon, path, color = "#2196F3" }) => (
    <Grid item xs={12} sm={6} md={4} lg={2.4}>
      <MDButton
        fullWidth
        variant="gradient"
        size="large"
        startIcon={icon}
        sx={{
          height: 90,
          fontSize: "0.85rem",
          fontWeight: 700,
          letterSpacing: "0.2px",
          whiteSpace: "normal",
          lineHeight: 1.2,
          borderRadius: "12px",
          color: "#FFFFFF !important",
          background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
          boxShadow: `0 4px 12px ${color}40`,
          transition: "all 0.25s ease",
          "&:hover": {
            background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
            transform: "translateY(-4px)",
            boxShadow: `0 8px 20px ${color}60`,
          },
          "& .material-icons-round, & .material-icons": {
            fontSize: "28px",
            color: "#FFFFFF !important",
          },
          "& .MuiSvgIcon-root": {
            fontSize: "28px",
            color: "#FFFFFF",
          },
        }}
        onClick={() => handleNavigate(path)}
      >
        {title}
      </MDButton>
    </Grid>
  );

  MenuCard.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.node,
    path: PropTypes.string.isRequired,
    color: PropTypes.string,
  };

  // ============================================================
  // CONTENT
  // ============================================================

  const renderContent = () => {
    switch (activeView) {
      // ========================================================
      // SETUP
      // ========================================================

      case "societyclient":
        return <SocietyClient />;

      case "client":
        return <Client />;

      // ========================================================
      // RBAC - MASTER
      // ========================================================

      case "rbac-modules":
        return <Modules />;

      case "rbac-module-tabs":
        return <ModuleTabs />;

      case "rbac-module-buttons":
        return <ModuleButtons />;

      case "rbac-button-actions":
        return <ButtonActions />;

      // ========================================================
      // RBAC - ROLES
      // ========================================================

      case "rbac-roles":
        return <Roles />;

      case "rbac-role-permissions":
        return <RolePermissionMatrix />;

      // ========================================================
      // RBAC - USER ACCESS
      // ========================================================

      case "rbac-user-colleges":
        return <UserCollegeAssignment />;

      case "rbac-user-college-roles":
        return <UserCollegeRoleAssignment />;
      case "rbac-users-college-roles":
        return <UserCollegeRoleBulkAssignment />;

      // ========================================================
      // HOME
      // ========================================================

      default:
        return (
          <>
            {/* ==================================================
                TABS
            ================================================== */}

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              TabIndicatorProps={{
                sx: {
                  backgroundColor: ["#2196F3", "#4CAF50", "#FF9800", "#9C27B0"][tabValue],
                  height: 5,
                  borderRadius: "5px 5px 0 0",
                },
              }}
            >
              {[
                {
                  label: "UPLOAD",
                  icon: <CloudUploadIcon />,
                  color: "#2196F3",
                  bgColor: "rgba(33, 150, 243, 0.10)",
                },
                {
                  label: "SETUP",
                  icon: <SettingsIcon />,
                  color: "#4CAF50",
                  bgColor: "rgba(76, 175, 80, 0.10)",
                },
                {
                  label: "ACTIONS",
                  icon: <TouchAppIcon />,
                  color: "#FF9800",
                  bgColor: "rgba(255, 152, 0, 0.12)",
                },
                {
                  label: "REPORTS",
                  icon: <AssessmentIcon />,
                  color: "#9C27B0",
                  bgColor: "rgba(156, 39, 176, 0.10)",
                },
              ].map((tab, index) => (
                <Tab
                  key={tab.label}
                  icon={tab.icon}
                  iconPosition="top"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.2,
                      }}
                    >
                      {tab.label}

                      {tabValue === index && (
                        <KeyboardArrowDownIcon
                          sx={{
                            fontSize: 30,
                            mt: 0.2,
                            color: tab.color,
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    minHeight: 105,
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#757575",
                    transition: "all 0.25s ease",
                    "& .MuiSvgIcon-root": {
                      fontSize: 30,
                      color: tab.color,
                      transition: "transform 0.25s ease",
                    },
                    "&:hover": {
                      backgroundColor: tab.bgColor,
                      "& .MuiSvgIcon-root": {
                        transform: "scale(1.12)",
                      },
                    },
                    "&.Mui-selected": {
                      color: tab.color,
                      fontWeight: 800,
                      backgroundColor: tab.bgColor,
                    },
                  }}
                />
              ))}
            </Tabs>

            {/* ==================================================
                UPLOAD
            ================================================== */}

            {tabValue === 0 && (
              <Grid container spacing={2}>
                {/* Add ERP upload modules here */}
              </Grid>
            )}

            {/* ==================================================
                SETUP
            ================================================== */}

            {tabValue === 1 && (
              <Grid container spacing={2}>
                <MenuCard
                  title="MANAGE CLIENT"
                  icon={<Icon>business</Icon>}
                  path="client"
                  color="#4CAF50"
                />

                <MenuCard
                  title="MANAGE COLLEGES"
                  icon={<Icon>account_balance</Icon>}
                  path="societyclient"
                  color="#009688"
                />
              </Grid>
            )}

            {/* ==================================================
                ACTIONS
            ================================================== */}

            {tabValue === 2 && (
              <>
                {/* ==================================================
                    RBAC MASTER
                ================================================== */}

                <MDTypography variant="h6" fontWeight="bold" mb={2} color="warning">
                  RBAC MASTERS
                </MDTypography>

                <Grid container spacing={2}>
                  <MenuCard
                    title="MODULES"
                    icon={<Icon>view_module</Icon>}
                    path="rbac-modules"
                    color="#FF9800"
                  />

                  <MenuCard
                    title="MODULE TABS"
                    icon={<Icon>tab</Icon>}
                    path="rbac-module-tabs"
                    color="#F4511E"
                  />

                  <MenuCard
                    title="MODULE BUTTONS"
                    icon={<Icon>smart_button</Icon>}
                    path="rbac-module-buttons"
                    color="#FB8C00"
                  />

                  <MenuCard
                    title="BUTTON ACTIONS"
                    icon={<Icon>touch_app</Icon>}
                    path="rbac-button-actions"
                    color="#E53935"
                  />
                </Grid>
                {/* ==================================================
                    ROLES
                ================================================== */}

                <MDTypography variant="h6" fontWeight="bold" mt={4} mb={2} color="primary">
                  ROLES & PERMISSIONS
                </MDTypography>

                <Grid container spacing={2}>
                  <MenuCard
                    title="MANAGE ROLES"
                    icon={<Icon>admin_panel_settings</Icon>}
                    path="rbac-roles"
                    color="#1976D2"
                  />

                  <MenuCard
                    title="ROLE PERMISSION MATRIX"
                    icon={<Icon>rule</Icon>}
                    path="rbac-role-permissions"
                    color="#3949AB"
                  />
                </Grid>

                {/* ==================================================
                    USER ACCESS
                ================================================== */}

                <MDTypography variant="h6" fontWeight="bold" mt={4} mb={2} color="success">
                  USER ACCESS
                </MDTypography>

                <Grid container spacing={2}>
                  <MenuCard
                    title="ASSIGN COLLEGES TO USER"
                    icon={<Icon>school</Icon>}
                    path="rbac-user-colleges"
                    color="#00897B"
                  />

                  <MenuCard
                    title="ASSIGN ROLES TO USER"
                    icon={<Icon>manage_accounts</Icon>}
                    path="rbac-user-college-roles"
                    color="#7B1FA2"
                  />

                  <MenuCard
                    title="ASSIGN COLLEGES, ROLES TO USERS"
                    icon={<Icon>manage_accounts</Icon>}
                    path="rbac-users-college-roles"
                    color="#5E35B1"
                  />
                </Grid>
              </>
            )}

            {/* ==================================================
                REPORTS
            ================================================== */}

            {tabValue === 3 && (
              <Grid container spacing={2}>
                {/* Add ERP reports here */}
              </Grid>
            )}
          </>
        );
    }
  };

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={2} pb={3}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                {/* HEADER - Matching the standard card design */}
                <MDBox
                  mx={2}
                  mt={-1}
                  py={1}
                  px={2}
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ color: "#FFFFFF" }}>settings_suggest</Icon>

                    <MDTypography variant="h6" color="white">
                      ERP SETUP
                    </MDTypography>
                  </MDBox>

                  {activeView !== "home" ? (
                    <MDButton
                      variant="contained"
                      onClick={handleBack}
                      startIcon={<Icon>arrow_back</Icon>}
                      sx={{
                        mb: 0,
                        color: "#FFFFFF",
                        backgroundColor: "rgba(255,255,255,0.18)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.28)",
                        },
                      }}
                    >
                      Back to ERP Setup
                    </MDButton>
                  ) : (
                    <MDButton
                      variant="contained"
                      onClick={() => navigate("/dashboard")}
                      startIcon={<Icon>dashboard</Icon>}
                      sx={{
                        mb: 0,
                        color: "#FFFFFF !important",
                        backgroundColor: "rgba(255,255,255,0.18) !important",
                        boxShadow: "none !important",
                        "& .material-icons": {
                          color: "#FFFFFF !important",
                        },
                        "&:hover": {
                          color: "#FFFFFF !important",
                          backgroundColor: "rgba(255,255,255,0.28) !important",
                          boxShadow: "none !important",
                        },
                        "&:focus": {
                          color: "#FFFFFF !important",
                          backgroundColor: "rgba(255,255,255,0.18) !important",
                        },
                        "&:active": {
                          backgroundColor: "rgba(255,255,255,0.28) !important",
                        },
                      }}
                    >
                      Dashboard
                    </MDButton>
                  )}
                </MDBox>

                {/* CONTENT */}
                <MDBox p={1}>{renderContent()}</MDBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>

      <MDBox display="flex" flexDirection="column" minHeight="50vh" />

      <Footer />
    </DashboardLayout>
  );
}

export default ERPSetup;
